<template>
  <TransitionRoot as="template" :show="show">
    <Dialog as="div" class="fixed inset-0 overflow-hidden z-50" @close="emitClose">
      <div class="absolute inset-0 overflow-hidden">
        <DialogOverlay class="absolute inset-0 bg-black opacity-40" />

        <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-4 sm:pl-8">
          <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-500 sm:duration-700"
            enter-from="translate-x-full"
            enter-to="translate-x-0"
            leave="transform transition ease-in-out duration-400 sm:duration-500"
            leave-from="translate-x-0"
            leave-to="translate-x-full"
          >
            <div class="pointer-events-auto w-screen max-w-md">
              <form class="flex h-full flex-col modalShell">
                <!-- HEADER -->
                <div class="shrink-0 modalHeader">
                  <div class="flex items-start justify-between gap-4">
                    <div class="min-w-0">
                      <DialogTitle class="text-lg font-semibold tracking-tight text-white">
                        Dodaj pozycję floty
                      </DialogTitle>
                      <p class="mt-1 text-sm text-slate-300">
                        Utwórz pojazd lub naczepę w kolekcji
                        <span class="font-semibold text-white">Fleet</span>.
                      </p>
                    </div>

                    <button
                      type="button"
                      @click.prevent="emitClose"
                      class="iconBtn shrink-0"
                      aria-label="Zamknij panel"
                    >
                      <svg class="h-4 w-4 text-slate-200" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path
                          fill-rule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- BODY -->
                <div class="min-h-0 flex-1 overflow-y-auto">
                  <div class="space-y-4 p-4 sm:p-5">
                    <!-- TYPE -->
                    <section class="sectionCard">
                      <div class="sectionHead">
                        <div>
                          <div class="sectionTitle">Typ pozycji</div>
                          <div class="sectionSub">
                            Ustal, czy dodajesz pojazd czy naczepę.
                          </div>
                        </div>
                      </div>

                      <div class="sectionBody">
                        <div class="inline-flex rounded-2xl border border-slate-700 bg-slate-900 p-1 shadow-inner overflow-hidden w-full">
                          <button
                            type="button"
                            @click.prevent="setVehicle(true)"
                            :class="[
                              'tabSwitchBtn flex-1',
                              form.vehicle ? 'tabSwitchBtn--active' : ''
                            ]"
                          >
                            Pojazd
                          </button>

                          <button
                            type="button"
                            @click.prevent="setVehicle(false)"
                            :class="[
                              'tabSwitchBtn flex-1',
                              !form.vehicle ? 'tabSwitchBtn--active' : ''
                            ]"
                          >
                            Naczepa
                          </button>
                        </div>

                        <p class="mt-3 text-xs text-slate-400">
                          Domyślny typ ustawiany jest na podstawie bieżącego widoku, ale możesz go zmienić ręcznie.
                        </p>
                      </div>
                    </section>

                    <!-- BASIC DATA -->
                    <section class="sectionCard">
                      <div class="sectionHead">
                        <div>
                          <div class="sectionTitle">Dane podstawowe</div>
                          <div class="sectionSub">
                            Uzupełnij najważniejsze informacje o pozycji floty.
                          </div>
                        </div>
                      </div>

                      <div class="sectionBody space-y-4">
                        <div class="fieldWrap">
                          <label class="fieldLabel">Numer rejestracyjny*</label>
                          <input
                            type="text"
                            maxlength="16"
                            v-model="form.licensePlate"
                            @blur="form.licensePlate = form.licensePlate?.toUpperCase().trim() || ''"
                            :class="inputCls(errors.licensePlate)"
                            placeholder="np. DW 12345"
                          />
                          <p v-if="errors.licensePlate" class="fieldError">
                            Podaj poprawną rejestrację (min. 3 znaki).
                          </p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Marka*</label>
                          <input
                            type="text"
                            maxlength="40"
                            v-model="form.brand"
                            :class="inputCls(errors.brand)"
                            placeholder="np. Scania"
                          />
                          <p v-if="errors.brand" class="fieldError">
                            Pole nie może być puste.
                          </p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Model</label>
                          <input
                            type="text"
                            maxlength="40"
                            v-model="form.model"
                            :class="inputCls(false)"
                            placeholder="np. R730"
                          />
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Aktywność</label>
                          <select
                            v-model="form.active"
                            class="selectField"
                          >
                            <option :value="true">Aktywny</option>
                            <option :value="false">Nieaktywny</option>
                          </select>
                        </div>
                      </div>
                    </section>

                    <!-- DOCUMENTS (tylko pojazd) -->
                    <section v-if="form.vehicle" class="sectionCard">
                      <div class="sectionHead">
                        <div>
                          <div class="sectionTitle">Dokumenty i GPS</div>
                          <div class="sectionSub">
                            Dane wymagane dla zgłoszeń SENT oraz rozliczeń przewozowych.
                          </div>
                        </div>
                      </div>

                      <div class="sectionBody space-y-4">
                        <div class="fieldWrap">
                          <label class="fieldLabel">
                            Numer zezwolenia drogowego / zaświadczenia / licencji*
                          </label>
                          <input
                            type="text"
                            maxlength="80"
                            v-model="form.roadPermissionNumber"
                            :class="inputCls(errors.roadPermissionNumber)"
                            placeholder="np. ITD/12345/2026"
                          />
                          <p v-if="errors.roadPermissionNumber" class="fieldError">
                            Podaj numer zezwolenia/zaświadczenia/licencji.
                          </p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Numer urządzenia / lokalizatora GPS*</label>
                          <input
                            type="text"
                            maxlength="80"
                            v-model="form.gpsTrackerNumber"
                            :class="inputCls(errors.gpsTrackerNumber)"
                            placeholder="np. GPS-000123"
                          />
                          <p v-if="errors.gpsTrackerNumber" class="fieldError">
                            Podaj numer urządzenia/lokalizatora GPS.
                          </p>
                        </div>
                      </div>
                    </section>

                    <!-- CHAMBERS -->
                    <section class="sectionCard">
                      <div class="sectionHead">
                        <div>
                          <div class="sectionTitle">
                            Komory cysterny
                            <span v-if="!form.vehicle" class="text-red-400 text-xs font-normal ml-1">(wymagane min. 1)</span>
                          </div>
                          <div class="sectionSub">
                            Dodaj komory i określ pojemność każdej z nich.
                            <template v-if="form.vehicle">
                              Dla ciągników (bez cysterny) możesz pominąć tę sekcję.
                            </template>
                            <template v-else>
                              Naczepa musi posiadać co najmniej jedną komorę.
                            </template>
                          </div>
                        </div>
                      </div>

                      <div class="sectionBody space-y-3">
                        <!-- Chamber list -->
                        <div
                          v-for="(chamber, idx) in form.chambers"
                          :key="idx"
                          class="chamberRow"
                        >
                          <div class="chamberIndex">{{ idx + 1 }}</div>
                          <div class="chamberInputWrap">
                            <input
                              type="number"
                              min="1"
                              max="100000"
                              step="1"
                              v-model="chamber.capacity"
                              :class="inputCls(errors.chambers && errors.chambers[idx])"
                              placeholder="Pojemność (L)"
                            />
                            <p v-if="errors.chambers && errors.chambers[idx]" class="fieldError">
                              Podaj pojemność od 1 do 100 000 L.
                            </p>
                          </div>
                          <button
                            type="button"
                            @click.prevent="removeChamber(idx)"
                            class="chamberRemoveBtn"
                            aria-label="Usuń komorę"
                          >
                            <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                          </button>
                        </div>

                        <p v-if="errors.chambersRequired" class="fieldError">
                          Naczepa wymaga co najmniej jednej komory.
                        </p>

                        <!-- Add chamber button -->
                        <button
                          type="button"
                          @click.prevent="addChamber"
                          class="addChamberBtn"
                        >
                          <svg class="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
                          </svg>
                          Dodaj komorę
                        </button>

                        <!-- Total capacity -->
                        <div class="capacitySummary" v-if="form.chambers.length > 0">
                          <div class="capacitySummaryLabel">Łączna pojemność zbiornika</div>
                          <div class="capacitySummaryValue">{{ totalCapacity.toLocaleString('pl-PL') }} L</div>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>

                <!-- FOOTER -->
                <div class="shrink-0 border-t border-slate-700 bg-slate-800 px-4 py-4">
                  <div class="flex items-center justify-between gap-3">
                    <div class="text-xs text-slate-400">
                      <span v-if="loading" class="text-slate-200">Zapisywanie danych...</span>
                      <span v-else>Sprawdź typ, rejestrację i dane pojazdu przed zapisaniem.</span>
                    </div>

                    <div class="flex items-center gap-2">
                      <button
                        @click.prevent="emitClose"
                        v-if="!loading"
                        type="button"
                        class="btnSecondary"
                      >
                        Zamknij
                      </button>

                      <button
                        ref="validateButton"
                        @click.prevent="validateAndSave"
                        v-if="!loading"
                        type="button"
                        class="btnPrimary"
                      >
                        Dodaj
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { db } from '@/firebase/gfbconf.js'

export default {
  name: 'ModalNewFleet',
  components: { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot },
  props: {
    show: { type: Boolean, default: false },
    showVehicles: { type: Boolean, default: true }
  },
  data() {
    return {
      loading: false,
      form: {
        active: true,
        brand: '',
        model: '',
        licensePlate: '',
        vehicle: true,
        chambers: [],
        roadPermissionNumber: '',
        gpsTrackerNumber: ''
      },
      errors: {
        brand: false,
        licensePlate: false,
        chambers: {},
        chambersRequired: false,
        roadPermissionNumber: false,
        gpsTrackerNumber: false
      }
    }
  },
  computed: {
    totalCapacity() {
      return this.form.chambers.reduce((sum, c) => {
        const val = Number(c.capacity)
        return sum + (isNaN(val) || val < 0 ? 0 : val)
      }, 0)
    }
  },
  mounted() {
    this.form.vehicle = this.showVehicles
  },
  watch: {
    show(val) {
      if (val) {
        this.form.vehicle = this.showVehicles
      }
    },
    showVehicles(val) {
      this.form.vehicle = val
    }
  },
  methods: {
    inputCls(hasError) {
      return [
        'inputField',
        hasError ? 'inputFieldError' : ''
      ]
    },

    setVehicle(v) {
      this.form.vehicle = v
    },

    addChamber() {
      this.form.chambers.push({ capacity: null })
    },

    removeChamber(idx) {
      this.form.chambers.splice(idx, 1)
      if (this.errors.chambers) {
        delete this.errors.chambers[idx]
      }
    },

    resetErrors() {
      this.errors.brand = false
      this.errors.licensePlate = false
      this.errors.chambers = {}
      this.errors.chambersRequired = false
      this.errors.roadPermissionNumber = false
      this.errors.gpsTrackerNumber = false
    },

    validate() {
      this.resetErrors()
      let errs = 0

      if (!this.form.licensePlate || this.form.licensePlate.trim().length < 3) {
        this.errors.licensePlate = true
        errs++
      }

      if (!this.form.brand || this.form.brand.trim().length === 0) {
        this.errors.brand = true
        errs++
      }

      // Pojazd wymaga zezwolenia drogowego i numeru GPS (SENT-105).
      if (this.form.vehicle) {
        if (!this.form.roadPermissionNumber || !this.form.roadPermissionNumber.trim()) {
          this.errors.roadPermissionNumber = true
          errs++
        }
        if (!this.form.gpsTrackerNumber || !this.form.gpsTrackerNumber.trim()) {
          this.errors.gpsTrackerNumber = true
          errs++
        }
      }

      // Trailer requires at least 1 chamber
      if (!this.form.vehicle && this.form.chambers.length === 0) {
        this.errors.chambersRequired = true
        errs++
      }

      // Validate each chamber capacity
      this.form.chambers.forEach((chamber, idx) => {
        const val = Number(chamber.capacity)
        if (
          chamber.capacity === '' ||
          chamber.capacity === null ||
          isNaN(val) ||
          val < 1 ||
          val > 100000
        ) {
          this.errors.chambers[idx] = true
          errs++
        }
      })

      return errs === 0
    },

    async validateAndSave() {
      if (!this.validate()) return
      this.loading = true

      try {
        const docRef = db.collection('Fleet').doc()

        const chambers = this.form.chambers.map((c, idx) => ({
          index: idx,
          capacity: Number(c.capacity)
        }))

        const payload = {
          id: docRef.id,
          active: !!this.form.active,
          brand: this.form.brand ? this.form.brand.trim() : '',
          model: this.form.model ? this.form.model.trim() : '',
          licensePlate: (this.form.licensePlate || '').toUpperCase().trim(),
          tankLitreCapacity: this.totalCapacity,
          chambers: chambers,
          vehicle: !!this.form.vehicle,
          meta: {
            created: {
              unix: Math.floor(Date.now() / 1000)
            }
          }
        }

        if (this.form.vehicle) {
          payload.roadPermissionNumber = (this.form.roadPermissionNumber || '').trim()
          payload.gpsTrackerNumber = (this.form.gpsTrackerNumber || '').trim()
        }

        await docRef.set(payload, { merge: false })

        this.$store.commit('setNotification', {
          show: true,
          head: 'Akcja zakończona sukcesem!',
          subheader: 'Pozycja floty została dodana.',
          success: true
        })

        this.emitClose()
      } catch (e) {
        console.error(e)
        this.$store.commit('setNotification', {
          show: true,
          head: 'Błąd zapisu do bazy',
          subheader: 'Spróbuj ponownie lub skontaktuj się z administratorem.',
          success: false
        })
        this.loading = false
      }
    },

    emitClose() {
      this.$emit('closeClientModal')
      this.clear()
    },

    clear() {
      this.loading = false
      this.resetErrors()
      this.form = {
        active: true,
        brand: '',
        model: '',
        licensePlate: '',
        vehicle: this.showVehicles,
        chambers: []
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.modalShell {
  height: 100%;
  background: rgba(30, 41, 59, 0.98);
  border-left: 1px solid rgba(71, 85, 105, 0.8);
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.35);
}

.modalHeader {
  padding: 20px 20px 18px 20px;
  border-bottom: 1px solid rgba(51, 65, 85, 1);
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.92));
}

.sectionCard {
  overflow: hidden;
  border: 1px solid rgba(71, 85, 105, 0.85);
  border-radius: 22px;
  background: rgba(30, 41, 59, 0.72);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  position: relative;
}

.sectionHead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px 14px 16px;
  border-bottom: 1px solid rgba(51, 65, 85, 0.9);
  background: rgba(51, 65, 85, 0.32);
}

.sectionTitle {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
}

.sectionSub {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.5;
  color: rgba(148, 163, 184, 1);
}

.sectionBody {
  padding: 16px;
}

.tabSwitchBtn {
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(148, 163, 184, 1);
  transition: all 0.18s ease;
}

.tabSwitchBtn:hover {
  background: rgba(51, 65, 85, 0.7);
  color: #e2e8f0;
}

.tabSwitchBtn--active {
  background: rgba(51, 65, 85, 1);
  color: #ffffff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
}

.fieldWrap {
  display: flex;
  flex-direction: column;
}

.fieldLabel {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #e2e8f0;
}

.fieldError {
  margin-top: 6px;
  font-size: 12px;
  color: #f87171;
}

.inputField,
.selectField {
  display: block;
  width: 100%;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 11px 14px;
  font-size: 14px;
  line-height: 1.3;
  transition: all 0.18s ease;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.inputField::placeholder {
  color: rgba(148, 163, 184, 0.75);
}

.inputField:focus,
.selectField:focus {
  outline: none;
  border-color: rgba(99, 102, 241, 0.85);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.inputFieldError {
  border-color: rgba(239, 68, 68, 0.9);
}

.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  width: 38px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(51, 65, 85, 0.8);
  transition: all 0.18s ease;
}

.iconBtn:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnSecondary,
.btnPrimary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 16px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 700;
  transition: all 0.18s ease;
}

.btnSecondary {
  border: 1px solid rgba(100, 116, 139, 0.8);
  background: rgba(51, 65, 85, 0.75);
  color: #f1f5f9;
}

.btnSecondary:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnPrimary {
  border: 1px solid rgba(59, 130, 246, 0.55);
  background: rgba(37, 99, 235, 0.22);
  color: #dbeafe;
}

.btnPrimary:hover {
  background: rgba(37, 99, 235, 0.3);
}

/* CHAMBERS */
.chamberRow {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.chamberIndex {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 44px;
  border-radius: 10px;
  background: rgba(51, 65, 85, 0.6);
  border: 1px solid rgba(71, 85, 105, 0.7);
  font-size: 12px;
  font-weight: 700;
  color: rgba(148, 163, 184, 1);
}

.chamberInputWrap {
  flex: 1;
  min-width: 0;
}

.chamberRemoveBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(127, 29, 29, 0.5);
  background: rgba(127, 29, 29, 0.18);
  color: #fca5a5;
  transition: all 0.18s ease;
}

.chamberRemoveBtn:hover {
  background: rgba(127, 29, 29, 0.35);
  color: #fecaca;
}

.addChamberBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px 14px;
  border-radius: 14px;
  border: 1px dashed rgba(100, 116, 139, 0.7);
  background: rgba(51, 65, 85, 0.2);
  color: rgba(148, 163, 184, 1);
  font-size: 12px;
  font-weight: 600;
  transition: all 0.18s ease;
}

.addChamberBtn:hover {
  background: rgba(51, 65, 85, 0.45);
  border-color: rgba(100, 116, 139, 1);
  color: #e2e8f0;
}

.capacitySummary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(51, 65, 85, 0.35);
  border: 1px solid rgba(71, 85, 105, 0.6);
  margin-top: 4px;
}

.capacitySummaryLabel {
  font-size: 12px;
  font-weight: 600;
  color: rgba(148, 163, 184, 1);
}

.capacitySummaryValue {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
}

@media (min-width: 640px) {
  .modalHeader {
    padding: 24px 24px 20px 24px;
  }

  .sectionBody,
  .sectionHead {
    padding-left: 18px;
    padding-right: 18px;
  }
}
</style>